// StrategyTemplates.jsx
import React from 'react';
import { Box, Paper, Typography, Grid, Chip, Button, Stack } from '@mui/material';
import { TrendingUp, Speed, ShowChart, BarChart } from '@mui/icons-material';

const STRATEGY_TEMPLATES = [
  {
    id: 'rsi-oversold',
    name: 'RSI Oversold/Overbought',
    description: 'Classic mean reversion strategy using RSI levels',
    icon: <BarChart />,
    difficulty: 'Beginner',
    buyConditions: [
      { indicator: 'RSI', params: { period: 14, source: 'close' }, operator: '<', compareType: 'number', value: '30' }
    ],
    sellConditions: [
      { indicator: 'RSI', params: { period: 14, source: 'close' }, operator: '>', compareType: 'number', value: '70' }
    ],
    profitStop: '10',
    lossStop: '5',
  },
  {
    id: 'ma-crossover',
    name: 'Moving Average Crossover',
    description: 'Buy when fast MA crosses above slow MA',
    icon: <ShowChart />,
    difficulty: 'Beginner',
    buyConditions: [
      { indicator: 'EMA', params: { period: 12, source: 'close' }, operator: '>', compareType: 'indicator', compareIndicator: 'EMA', compareParams: { period: 26, source: 'close' } }
    ],
    sellConditions: [
      { indicator: 'EMA', params: { period: 12, source: 'close' }, operator: '<', compareType: 'indicator', compareIndicator: 'EMA', compareParams: { period: 26, source: 'close' } }
    ],
    profitStop: '15',
    lossStop: '7',
  },
  {
    id: 'rsi-volume',
    name: 'RSI + Volume Confirmation',
    description: 'RSI signals confirmed by above-average volume',
    icon: <TrendingUp />,
    difficulty: 'Intermediate',
    buyConditions: [
      { indicator: 'RSI', params: { period: 14, source: 'close' }, operator: '<', compareType: 'number', value: '35', combinator: null },
      { indicator: 'Volume', params: {}, operator: '>', compareType: 'number', value: '100000', combinator: 'AND' }
    ],
    sellConditions: [
      { indicator: 'RSI', params: { period: 14, source: 'close' }, operator: '>', compareType: 'number', value: '65' }
    ],
    profitStop: '12',
    lossStop: '6',
  },
  {
    id: 'tick-scalping',
    name: 'Tick Scalping',
    description: 'High-frequency strategy using tick data',
    icon: <Speed />,
    difficulty: 'Advanced',
    requiresTick: true,
    buyConditions: [
      { indicator: 'tickRSI', params: { period: 10 }, operator: '<', compareType: 'number', value: '25', combinator: null },
      { indicator: 'tickPrice', params: {}, operator: '>', compareType: 'indicator', compareIndicator: 'tickVWAP', compareParams: {}, combinator: 'AND' }
    ],
    sellConditions: [
      { indicator: 'tickRSI', params: { period: 10 }, operator: '>', compareType: 'number', value: '75' }
    ],
    profitStop: '2',
    lossStop: '1',
  },
];

export default function StrategyTemplates({ onApply, isTickMode }) {
  const filteredTemplates = STRATEGY_TEMPLATES.filter(t => 
    isTickMode ? t.requiresTick : !t.requiresTick
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
        Quick Start Templates
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select a template to auto-fill your strategy, then customize as needed.
      </Typography>
      
      <Grid container spacing={2}>
        {filteredTemplates.map(template => (
          <Grid item xs={12} sm={6} key={template.id}>
            <Paper
              variant="outlined"
              sx={{
                p: 2,
                cursor: 'pointer',
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: 'primary.main',
                  boxShadow: 2,
                  transform: 'translateY(-2px)',
                },
              }}
              onClick={() => onApply(template)}
            >
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
                <Box sx={{ 
                  p: 1, 
                  borderRadius: 1.5, 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText',
                  display: 'flex'
                }}>
                  {template.icon}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {template.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {template.description}
                  </Typography>
                  <Box sx={{ mt: 1 }}>
                    <Chip 
                      size="small" 
                      label={template.difficulty}
                      color={
                        template.difficulty === 'Beginner' ? 'success' :
                        template.difficulty === 'Intermediate' ? 'warning' : 'error'
                      }
                      variant="outlined"
                      sx={{ fontSize: '0.65rem', height: 20 }}
                    />
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Paper>
  );
}